import { useCallback, useEffect, useRef, useState } from 'react';

/** Where the next page starts: an opaque server cursor, or a row offset. */
export interface InfinitePosition {
  cursor?: string;
  offset: number;
}

export interface InfinitePage<T> {
  items: readonly T[];
  /** Cursor of the next page; null/undefined at the end. Takes precedence over `nextOffset`. */
  nextCursor?: string | null;
  /** Offset of the next page; null = end. Omitted (with no cursor field): derived from `total`, else the end. */
  nextOffset?: number | null;
  total?: number;
}

export interface InfiniteListOptions<T> {
  /** Loads the page at `position`; must honour `signal` (aborted when the query changes). */
  load: (position: InfinitePosition, signal: AbortSignal) => Promise<InfinitePage<T>>;
  /** Identifies the query (folder, search text, filters). A change drops the pages once the first page of the new query arrived. */
  queryKey: string;
  /** Stable row id: rows that appear in two pages (the list shifted meanwhile) are shown once. */
  getId?: (item: T) => string | number;
  /** False: nothing is requested (lazy lists). Default true. */
  enabled?: boolean;
  /** Wait this long before requesting the first page of a new query (typing in a search box). Default 0. */
  debounceMs?: number;
}

export interface InfiniteList<T> {
  /** Rows loaded so far. Kept (stale) while the next query or a reload is fetched. */
  items: T[];
  /** No rows to show yet and the first page is on its way (show a skeleton only now). */
  loading: boolean;
  /** Rows of an earlier query/reload are shown while the current first page loads. */
  refreshing: boolean;
  loadingMore: boolean;
  /** Error of the current query's first page or of the last "more" request. */
  error: unknown;
  hasMore: boolean;
  /** Total from the server when it sent one. */
  total: number | undefined;
  /** Requests the next page. No-op while one is in flight, at the end, or before the first page. Safe to call repeatedly. */
  loadMore: () => void;
  /** Fetches the first page again; the current rows stay visible until it arrives. */
  reload: () => void;
}

interface Loaded<T> {
  /** Request that produced the first page. */
  requestKey: string;
  items: T[];
  next: InfinitePosition | null;
  total: number | undefined;
}

function nextPosition(page: InfinitePage<unknown>, loadedRows: number): InfinitePosition | null {
  if (page.nextCursor) return { cursor: page.nextCursor, offset: loadedRows };
  if (page.nextCursor === null) return null;
  if (page.nextOffset !== undefined) return page.nextOffset === null ? null : { offset: page.nextOffset };
  return page.total !== undefined && page.items.length > 0 && loadedRows < page.total ? { offset: loadedRows } : null;
}

function merge<T>(known: readonly T[], added: readonly T[], getId?: (item: T) => string | number): T[] {
  if (!getId) return [...known, ...added];
  const seen = new Set(known.map(getId));
  return [...known, ...added.filter((item) => !seen.has(getId(item)))];
}

/**
 * Infinite list over a paged server endpoint (cursor or offset): the one hook behind every long list.
 *
 * - Query change or `reload()` starts again at the first page; the old rows stay visible until it arrives
 *   (stale-while-revalidate), so only the very first load shows a skeleton.
 * - `loadMore` ignores calls while a page is in flight, so a scroll observer may call it freely (pair it with
 *   `InfiniteScrollSentinel`, which asks before the user reaches the end).
 * - Requests of a replaced query are aborted and their answers dropped.
 * - A failed "more" request keeps the rows and sets `error`; calling `loadMore` again retries.
 */
export function useInfiniteList<T>({ load, queryKey, getId, enabled = true, debounceMs = 0 }: InfiniteListOptions<T>): InfiniteList<T> {
  const [reloadTick, setReloadTick] = useState(0);
  const requestKey = `${queryKey}\u0000${reloadTick}`;
  const [loaded, setLoaded] = useState<Loaded<T>>();
  const [failure, setFailure] = useState<{ requestKey: string; error: unknown }>();
  const [loadingMore, setLoadingMore] = useState(false);
  const callbacks = useRef({ load, getId });
  useEffect(() => {
    callbacks.current = { load, getId };
  });
  const moreInFlight = useRef(false);
  const moreAbort = useRef<AbortController>(undefined);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      callbacks.current.load({ offset: 0 }, controller.signal).then(
        (page) => {
          if (controller.signal.aborted) return;
          const items = merge([], page.items, callbacks.current.getId);
          setLoaded({ requestKey, items, next: nextPosition(page, page.items.length), total: page.total });
          setFailure(undefined);
        },
        (error: unknown) => {
          if (!controller.signal.aborted) setFailure({ requestKey, error });
        },
      );
    }, debounceMs);
    return () => {
      clearTimeout(timer);
      controller.abort();
      moreAbort.current?.abort();
      moreInFlight.current = false;
      setLoadingMore(false);
    };
  }, [enabled, requestKey, debounceMs]);

  const current = loaded?.requestKey === requestKey ? loaded : undefined;
  const next = current?.next ?? null;
  const loadMore = useCallback(() => {
    if (!current || next === null || moreInFlight.current) return;
    moreInFlight.current = true;
    setLoadingMore(true);
    setFailure(undefined);
    const controller = new AbortController();
    moreAbort.current = controller;
    callbacks.current.load(next, controller.signal).then(
      (page) => {
        if (controller.signal.aborted) return;
        setLoaded((state) => {
          if (state?.requestKey !== requestKey) return state;
          const items = merge(state.items, page.items, callbacks.current.getId);
          return { ...state, items, next: nextPosition(page, items.length), total: page.total ?? state.total };
        });
      },
      (error: unknown) => {
        if (!controller.signal.aborted) setFailure({ requestKey, error });
      },
    ).finally(() => {
      if (controller.signal.aborted) return;
      moreInFlight.current = false;
      setLoadingMore(false);
    });
  }, [current, next, requestKey]);

  const reload = useCallback(() => setReloadTick((tick) => tick + 1), []);
  const currentFailure = failure?.requestKey === requestKey ? failure.error : undefined;
  const settled = current !== undefined || currentFailure !== undefined;
  return {
    items: loaded?.items ?? [],
    loading: enabled && !settled && loaded === undefined,
    refreshing: enabled && !settled && loaded !== undefined,
    loadingMore,
    error: currentFailure,
    hasMore: next !== null,
    total: loaded?.total,
    loadMore,
    reload,
  };
}
